/*
 * File: user.slice.js
 * Project: lets-talk-web
 *
 * Created by Brendan Michaelsen on January 29, 2022 at 1:53 PM
 * Copyright © 2022 Let's Talk. All rights reserved.
 *
 * Last Modified: April 8, 2024 at 9:38 PM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

// Services
import { getCurrentUser } from '../../../services/user';

// Constants
import { ROLES } from '../../../../Constants';


/**
 * Functions
 */

const setUserForAnalytics = (user) => {

	// Only set user for analytics if not impersonated
	if (!user.isImpersonation) {

		// Set current user for GTM
		try {
			window.dataLayer.push({ user_id: user.id });
		} catch (e) {}
	}
};

export const fetchUser = createAsyncThunk('user/fetchUser', async () => {
	try {

		// Fetch current user
		const { data } = await getCurrentUser();

		// Set current user for analytics
		setUserForAnalytics(data.user);

		// Return user
		return data.user;

	} catch (e) {

		// Return null
		return null;
	}
});


/**
 * Slices
 */

export const userSlice = createSlice({
	name: 'user',
	initialState: {
		status: 'idle',
		error: null,
		value: null,
		impersonationInProgress: false
	},
	reducers: {
		updateUser: (state, action) => {

			// Set state
			state.value = action.payload;
			state.status = 'succeeded';

			// Set current user for analytics
			setUserForAnalytics(action.payload);
		},
		clearUser: (state) => {

			// Clear state
			state.value = { isActive: true, isAnonymous: true, role: { primary: [ROLES.PARENT, ROLES.ADMIN] } };

			// Clear current user from GTM
			try {
				window.dataLayer.push({ user_id: undefined });
			} catch (e) {}
		},
	},
	extraReducers: {
		[fetchUser.pending]: (state) => {
			state.status = 'loading';
		},
		[fetchUser.fulfilled]: (state, action) => {
			state.status = 'succeeded';
			state.value = action.payload;
		},
		[fetchUser.rejected]: (state, action) => {
			state.status = 'failed';
			state.error = action.error.message;
		},
	},
});


/**
 * Exports
 */

export const { updateUser, clearUser } = userSlice.actions;

export default userSlice.reducer;
